import * as React from 'react';
import styled, { keyframes } from 'styled-components';

import { composeRefs } from '../../internals/composeRefs';
import type { FloatingAlign, FloatingSide } from '../../internals/computeFloatingPosition';
import { createUIContext } from '../../internals/createContext';
import { getSemanticAttributes } from '../../internals/semanticProps';
import { Presence } from '../../internals/Presence';
import { Portal } from '../../internals/Portal';
import { useClickOutside } from '../../internals/useClickOutside';
import { useControllable } from '../../internals/useControllable';
import { useEscapeKey } from '../../internals/useEscapeKey';
import { useFloatingPosition } from '../../internals/useFloatingPosition';
import { palette } from '../../tokens/palette';
import { rem } from '../../tokens/spacing';
import { zIndex } from '../../tokens/zIndex';

/* ─────────────────────────── Types ─────────────────────────────────────── */

export type FilterPanelSize = 'sm' | 'md' | 'lg' | 'xl';

export interface FilterRootProps {
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: FloatingSide;
  align?: FloatingAlign;
  children: React.ReactNode;
}

interface FilterContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerRef: React.MutableRefObject<HTMLElement | null>;
  panelRef: React.MutableRefObject<HTMLDivElement | null>;
}

const [FilterProvider, useFilterContext] = createUIContext<FilterContextValue>('Filter');

/* ─────────────────────────── Root ──────────────────────────────────────── */

const FilterRoot = ({
  open: openProp,
  defaultOpen,
  onOpenChange,
  children,
}: FilterRootProps) => {
  const [open, setOpen] = useControllable<boolean>({
    value: openProp,
    defaultValue: defaultOpen ?? false,
    onChange: onOpenChange,
  });
  const triggerRef = React.useRef<HTMLElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);

  const value = React.useMemo(
    () => ({ open, onOpenChange: setOpen, triggerRef, panelRef }),
    [open, setOpen],
  );

  return <FilterProvider value={value}>{children}</FilterProvider>;
};

FilterRoot.displayName = 'Filter.Root';

/* ─────────────────────────── Trigger ──────────────────────────────────── */

const StyledTriggerButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: ${rem(4)};
  padding: ${rem(9)} ${rem(19)};
  border: ${rem(1)} solid var(--ui-border, ${palette.LightGray});
  border-radius: var(--ui-radius-md, ${rem(6)});
  background: var(--ui-surface, ${palette.White});
  color: var(--ui-text, ${palette.Black});
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);
  font-size: ${rem(16)};
  font-weight: 500;
  line-height: ${rem(22)};
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  box-shadow: 0 ${rem(2)} ${rem(4)} rgba(203, 186, 201, 0.16);

  .material-symbols-outlined {
    font-size: ${rem(20)};
    line-height: 1;
  }

  &:not(:disabled):hover {
    border-color: var(--ui-text-subtle, ${palette.SpanishGray});
    box-shadow: 0 ${rem(4)} ${rem(6)} rgba(179, 179, 179, 0.24);
  }

  &:disabled {
    background: var(--ui-background-muted, ${palette.OffWhite});
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export interface FilterTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  semanticId?: string;
}

const FilterTrigger = React.forwardRef<HTMLButtonElement, FilterTriggerProps>(
  function FilterTrigger({ semanticId, onClick, type = 'button', children, ...rest }, ref) {
    const { open, onOpenChange, triggerRef } = useFilterContext();
    const semanticProps = getSemanticAttributes(semanticId);
    const composedRef = React.useMemo(() => composeRefs(ref, triggerRef), [ref, triggerRef]);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      onClick?.(event);
      if (!event.defaultPrevented) onOpenChange(!open);
    };

    return (
      <StyledTriggerButton
        ref={composedRef}
        type={type}
        aria-haspopup="true"
        aria-expanded={open}
        data-state={open ? 'open' : 'closed'}
        onClick={handleClick}
        {...semanticProps}
        {...rest}
      >
        {children ?? (
          <span className="material-symbols-outlined" aria-hidden>
            tune
          </span>
        )}
      </StyledTriggerButton>
    );
  },
);

FilterTrigger.displayName = 'Filter.Trigger';

/* ─────────────────────────── Panel ────────────────────────────────────── */

const PANEL_WIDTHS: Record<FilterPanelSize, string> = {
  sm: '400px',
  md: '600px',
  lg: '768px',
  xl: '900px',
};

const panelSlideIn = keyframes`
  from { opacity: 0; transform: translateY(${rem(-4)}); }
  to   { opacity: 1; transform: translateY(0); }
`;
const panelSlideOut = keyframes`
  from { opacity: 1; transform: translateY(0); }
  to   { opacity: 0; transform: translateY(${rem(-4)}); }
`;

const StyledPanel = styled.div<{ $size: FilterPanelSize }>`
  position: fixed;
  z-index: ${zIndex.filter};
  display: flex;
  flex-direction: column;
  width: ${({ $size }) => PANEL_WIDTHS[$size]};
  max-width: calc(100vw - ${rem(32)});
  background: var(--ui-surface, ${palette.White});
  border-radius: var(--ui-radius-md, ${rem(6)});
  box-shadow: 0 ${rem(2)} ${rem(20)} var(--ui-shadow-default, rgba(109, 109, 109, 0.15));
  padding: ${rem(21)};
  font-family: var(--ui-font-sans, 'HK Grotesk', sans-serif);

  &[data-state='open'] {
    animation: ${panelSlideIn} 200ms ease-out forwards;
  }
  &[data-state='closed'] {
    animation: ${panelSlideOut} 150ms ease-in forwards;
  }
`;

export interface FilterPanelProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: FilterPanelSize;
  side?: FloatingSide;
  align?: FloatingAlign;
  semanticId?: string;
}

const FilterPanel = React.forwardRef<HTMLDivElement, FilterPanelProps>(
  function FilterPanel(
    { size = 'md', side = 'bottom', align = 'start', semanticId, style, children, ...rest },
    ref,
  ) {
    const { open, onOpenChange, triggerRef, panelRef } = useFilterContext();
    const semanticProps = getSemanticAttributes(semanticId);

    const close = React.useCallback(() => onOpenChange(false), [onOpenChange]);

    useClickOutside(panelRef, close, open, {
      ignoredRefs: [triggerRef],
      ignoredSelectors: ['[id^="wl-datepicker-body-"]'],
    });
    useEscapeKey(close, open);

    const { coords } = useFloatingPosition({
      open,
      side,
      align,
      offsetPx: 10,
      triggerRef,
      floatingRef: panelRef,
    });

    const composedRef = React.useMemo(() => composeRefs(ref, panelRef), [ref, panelRef]);

    const positioned = coords.top > -9000;

    return (
      <Portal>
        <Presence present={open}>
          <StyledPanel
            ref={composedRef}
            $size={size}
            data-state={open && positioned ? 'open' : 'closed'}
            style={{
              top: coords.top,
              left: coords.left,
              visibility: positioned ? 'visible' : 'hidden',
              ...style,
            }}
            {...semanticProps}
            {...rest}
          >
            {children}
          </StyledPanel>
        </Presence>
      </Portal>
    );
  },
);

FilterPanel.displayName = 'Filter.Panel';

/* ─────────────────────────── Header ───────────────────────────────────── */

const StyledHeader = styled.div`
  font-weight: 500;
  font-size: ${rem(14)};
  line-height: ${rem(18)};
  letter-spacing: ${rem(4)};
  margin-bottom: ${rem(14)};
  text-transform: uppercase;
  color: var(--ui-text, ${palette.Black});
`;

export interface FilterHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const FilterHeader = React.forwardRef<HTMLDivElement, FilterHeaderProps>(
  function FilterHeader({ semanticId, children, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return (
      <StyledHeader ref={ref} {...semanticProps} {...rest}>
        {children ?? 'Filter By:'}
      </StyledHeader>
    );
  },
);

FilterHeader.displayName = 'Filter.Header';

/* ─────────────────────────── Grid ─────────────────────────────────────── */

const StyledGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
  gap: ${rem(16)} ${rem(20)};
`;

export interface FilterGridProps extends React.HTMLAttributes<HTMLDivElement> {
  columns?: number;
  semanticId?: string;
}

const FilterGrid = React.forwardRef<HTMLDivElement, FilterGridProps>(
  function FilterGrid({ columns = 2, semanticId, children, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return (
      <StyledGrid ref={ref} $columns={columns} {...semanticProps} {...rest}>
        {children}
      </StyledGrid>
    );
  },
);

FilterGrid.displayName = 'Filter.Grid';

/* ─────────────────────────── Actions ──────────────────────────────────── */

const StyledActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  margin-top: ${rem(16)};
`;

export interface FilterActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  semanticId?: string;
}

const FilterActions = React.forwardRef<HTMLDivElement, FilterActionsProps>(
  function FilterActions({ semanticId, children, ...rest }, ref) {
    const semanticProps = getSemanticAttributes(semanticId);
    return (
      <StyledActions ref={ref} {...semanticProps} {...rest}>
        {children}
      </StyledActions>
    );
  },
);

FilterActions.displayName = 'Filter.Actions';

/* ─────────────────────────── Compound export ──────────────────────────── */

export const Filter = {
  Root: FilterRoot,
  Trigger: FilterTrigger,
  Panel: FilterPanel,
  Header: FilterHeader,
  Grid: FilterGrid,
  Actions: FilterActions,
};
